import { useNavigate } from "react-router";
import {
  createBannerURL,
  createIconURL,
  createPostRevisionPageURL,
  createPostURL,
  createProfileTagURL,
  createProfilePageURL,
} from "#lib/urls";
import { ImageBackground, ImageLink } from "#components/images";
import { KemonoLink } from "#components/links";
import { IPaySite } from "#entities/paysites";
import { IPostActionsProps, IPostOverviewProps } from "./types";
import { FlagButton } from "./flag-button";
import { Timestamp } from "#components/dates";
import { FavoriteButton } from "#components/favorite-button";
import { useLayoutEffect, useRef, useState } from "react";

interface IPostHeaderProps
  extends Pick<
    IPostOverviewProps,
    "post" | "creator" | "revisions" | "flagged"
  > {
  postTitle: string;
  paysite: IPaySite;
}

export function PostHeader({
  post,
  creator,
  postTitle,
  paysite,
  revisions,
  flagged,
}: IPostHeaderProps) {
  const navigate = useNavigate();
  const profileURL = String(
    createProfilePageURL({ service: post.service, creatorId: post.user })
  );
  const profileIcon = createIconURL(post.service, post.user);
  const bannerURL = createBannerURL(post.service, post.user);

  if (typeof post.tags === "string") {
    post.tags = post.tags
      .slice(1, -1) // remove outer quotes/brackets
      .split(",")   // split into tags
      .map(tag => tag.trim().replace(/^"|"$/g, "")); // strip surrounding quotes
  }

  return (
    <header className="post__header">
      <div className="post__user">
        <ImageBackground src={bannerURL} />
        <ImageLink
          url={profileURL}
          src={profileIcon}
          isLazy={false}
          className="post__user-profile"
        />

        <div>
          <KemonoLink className="post__user-name" url={profileURL}>
            {!creator ? `Profile page` : creator.name}
          </KemonoLink>
        </div>
      </div>

      <div className="post__info">
        <h1 className="post__title">
          <span>{postTitle}</span> <span>({paysite.title})</span>
        </h1>

        {!post.published ? undefined : (
          <div className="post__published" style={{ margin: "0.125rem 0" }}>
            <div style={{ width: "89px", display: "inline-block" }}>
              Published:
            </div>{" "}
            <Timestamp time={post.published} />
          </div>
        )}

        {!post.edited || post.edited === post.published ? undefined : (
          <div className="post__edited" style={{ margin: "0.125rem 0" }}>
            <div style={{ display: "inline-block", width: "89px" }}>
              Edited:
            </div>{" "}
            <Timestamp time={post.edited} />
          </div>
        )}

        <div className="post__added" style={{ margin: "0.125rem 0" }}>
          {revisions.length < 2 ? (
            <span>
              <div style={{ width: "89px", display: "inline-block" }}>
                Imported:{" "}
              </div>{" "}
              {post.added?.slice(0, 7) ?? "Unknown"}
            </span>
          ) : (
            <>
              <div
                style={{
                  display: "inline-block",
                  width: "85px",
                  margin: "0",
                }}
              >
                Imported:
              </div>
              <span
                className="post__revisions"
                title="View past versions of this page"
              >
                <select
                  id="post-revision-selection"
                  style={{ textAlignLast: "left" }}
                  defaultValue={post.revision_id}
                  onChange={(event) => {
                    const revisionID = event.currentTarget.value;

                    if (revisionID) {
                      navigate(
                        String(
                          createPostRevisionPageURL(
                            post.service,
                            post.user,
                            post.id,
                            revisionID
                          )
                        )
                      );
                    } else {
                      navigate(
                        String(createPostURL(post.service, post.user, post.id))
                      );
                    }
                  }}
                >
                  {revisions.map(([index, postRevision]) => (
                    <option key={index} value={postRevision.revision_id ?? ""}>
                      {postRevision.added?.slice(0, 7) ?? "Unknown"} {"<"}
                      {index}
                      {">"} [{postRevision.revision_id ?? "current"}]
                    </option>
                  ))}
                </select>
              </span>
            </>
          )}
        </div>

        <TagList tags={post.tags} creatorId={post.user} service={post.service} />

        <PostActions
          service={post.service}
          creatorId={post.user}
          postId={post.id}
          flagged={flagged}
        />
      </div>
    </header>
  );
}

function PostActions({
  service,
  creatorId,
  postId,
  flagged,
}: IPostActionsProps) {
  return (
    <div className="post__actions">
      <FavoriteButton service={service} creatorId={creatorId} postId={postId} />
      <FlagButton service={service} creatorId={creatorId} postId={postId} flagged={flagged} />
    </div>
  );
}

function TagList({ tags, service, creatorId}: { tags: string[] | null; service: string; creatorId: string }) {
  const [showAll, setShowAll] = useState(false);
  const [isOverflow, setIsOverflow] = useState(false);
  const element = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const div = element.current;
    setIsOverflow(!!(div && (div.offsetWidth < div.scrollWidth)));
  }, [element]);

  return tags && tags.length !== 0 && (
    <section id="post-tags">
      <span id="label">Tags: </span>
      <div className={showAll ? "show-overflow" : ""} ref={element}>
        {tags.map((tag, index) => (
          <a
            key={index}
            href={String(
              createProfileTagURL(service, creatorId, tag)
            )}
          >
            {tag}
          </a>
        ))}
      </div>
      <a id="show-tag-overflow-button" href="javascript:void 0" onClick={_e => setShowAll(val => !val)} hidden={!isOverflow}>
        {showAll ? "« Hide" : "Show all »"}
      </a>
    </section>
  );
}
